// Firebase Storage 업로드/다운로드/삭제 헬퍼.
// 파일 메타데이터(이름·크기·URL)는 Firestore 컬렉션에 별도 보관해야 목록 조회가 쉬움.
// 본 모듈은 Storage 부분만 책임지고, Firestore 메타 보관은 page-level 에서 처리한다.

import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref as storageRef,
  uploadBytes,
  type StorageReference,
  type UploadResult
} from 'firebase/storage';
import { v4 as uuid } from 'uuid';

import { storage } from 'firebase/config';

export interface UploadedFile {
  fileId: string;        // 클라이언트가 생성한 식별자 (메타 doc id 로도 사용 가능)
  storagePath: string;   // Storage 안의 절대 경로 — 삭제 시 필요
  downloadUrl: string;   // 브라우저 다운로드/표시용 URL
  name: string;
  size: number;
  contentType: string;
}

// userId 별로 분리된 디렉토리에 업로드. 파일 식별자는 uuid v4 로 만들어 충돌 방지.
export async function uploadFile(userId: string, file: File): Promise<UploadedFile> {
  if (file.size === 0) throw new Error(`빈 파일(0바이트)은 업로드할 수 없습니다: ${file.name}`);
  const fileId = uuid();
  const path = `uploads/${userId}/${fileId}/${file.name}`;
  const ref = storageRef(storage, path);
  const result: UploadResult = await uploadBytes(ref, file, { contentType: file.type });
  const downloadUrl = await getDownloadURL(result.ref);

  return {
    fileId,
    storagePath: path,
    downloadUrl,
    name: file.name,
    size: file.size,
    contentType: file.type
  };
}

export async function deleteFile(storagePath: string): Promise<void> {
  await deleteObject(storageRef(storage, storagePath));
}

// 사건 폴더(`<appraisalDocId>/`) 아래 모든 파일을 삭제 — 사건 삭제 시 첨부 동반 정리.
// 폴더 단위로 훑으므로 file_info 에 등록되지 않은 잔존 파일까지 함께 회수된다.
// 일부 삭제가 실패해도 throw 하지 않고 집계만 반환(호출부에서 경고). 잔존분은 cleanup-orphans 로 회수 가능.
export async function deleteAllAppraisalFiles(
  appraisalDocId: string
): Promise<{ deleted: number; failed: number }> {
  let deleted = 0;
  let failed = 0;
  const walk = async (dir: StorageReference): Promise<void> => {
    const res = await listAll(dir); // 없는 폴더면 빈 결과 (throw 안 함)
    for (const item of res.items) {
      try {
        await deleteObject(item);
        deleted++;
      } catch {
        failed++;
      }
    }
    for (const sub of res.prefixes) await walk(sub);
  };
  await walk(storageRef(storage, appraisalDocId));
  return { deleted, failed };
}

// appraisals 전용 — geek1 과 같은 경로 패턴 (`<appraisalDocId>/<timestamp>_<filename>`)
// 그래야 옛 188건의 첨부도 같은 Storage 에서 접근 가능 (05 §4, 06 §2.2.4).
export async function uploadAppraisalFile(
  appraisalDocId: string,
  file: File
): Promise<{ storagePath: string }> {
  if (file.size === 0) throw new Error(`빈 파일(0바이트)은 첨부할 수 없습니다: ${file.name}`);
  const path = `${appraisalDocId}/${Date.now()}_${file.name}`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, file, {
    contentType: file.type,
    customMetadata: { originFileName: file.name }
  });
  return { storagePath: path };
}

// Storage path → downloadURL (Firestore 의 file_info[].url 은 path 형태)
export async function getAppraisalFileDownloadURL(storagePath: string): Promise<string> {
  return getDownloadURL(storageRef(storage, storagePath));
}
